// ─── Committee report ──────────────────────────────────────────────────
// Renders the AnnualSscSummary aggregate — the report buckets, four
// leaderboards and the SC evaluations table — for whatever period the page's Period filter currently
// selects. Annual / quarterly / monthly are all just different
// date_from/date_to windows on the same filtered row set (Task 16's
// period presets), so this is one component with no cadence switches of
// its own — the caller decides the window, this just renders the result.
//
// Styling mirrors the rest of the Overview page: MetaTile-style stat tiles
// for the headline figures (overline label + prominent tabular number),
// `overline` sub-section headers, and the same soft divider / spacing
// rhythm as BreakdownCard. Two clear levels of hierarchy — the card title
// (strong, mixed-case) sits above the uppercase `overline` sub-headers.

import { useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Table, type DataTableColumn } from "../../ds";
import { displayDepartment } from "../../api/departments";
import {
  REPORT_BUCKETS,
  type AnnualSscSummary,
  type ScEvaluationRow,
} from "./utils";

interface AnnualSscReportProps {
  summary: AnnualSscSummary;
  /** Human label for the active period, e.g. "This Quarter", "All Time". */
  periodLabel: string;
}

// Leaderboards can run long on a mature account — collapse to the top N
// so the section doesn't dwarf the rest of the page, with a "Show more"
// toggle to expand in place (mirrors BreakdownCard's overflow behavior).
// The full, uncapped data also goes into the XLSX export (Reports.tsx
// builds those sheets straight from the same AnnualSscSummary).
const LEADERBOARD_LIMIT = 10;

// A single headline figure, styled like the page's MetaTile (bordered,
// overline label above a prominent tabular number).
function SummaryStat({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        // Grid rows stretch every tile to the tallest; pushing the value
        // to the bottom keeps the figures on one line when a label wraps.
        justifyContent: "space-between",
        gap: 0.75,
        minWidth: 0,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.1,
          color: "text.primary",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function Leaderboard({
  title,
  caption,
  rows,
  emptyHint,
}: {
  title: string;
  /**
   * What the number column counts. The titles alone don't say whether a
   * board ranks approvals or submissions — "Top Units" and "Top
   * Departments" sit side by side and count different populations — so
   * every board states it, which also keeps their first rows aligned
   * across the grid.
   */
  caption: string;
  rows: { label: string; value: number | string }[];
  emptyHint: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = rows.length > LEADERBOARD_LIMIT;
  const visible =
    hasOverflow && !expanded ? rows.slice(0, LEADERBOARD_LIMIT) : rows;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {caption}
      </Typography>
      {visible.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {emptyHint}
        </Typography>
      ) : (
        <Box>
          {visible.map((row, idx) => (
            <Stack
              key={`${row.label}-${idx}`}
              direction="row"
              alignItems="center"
              gap={1.25}
              sx={{
                py: 0.85,
                borderBottom:
                  idx === visible.length - 1 ? "none" : "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  width: 22,
                  flexShrink: 0,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {idx + 1}.
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={row.label}
              >
                {row.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  flexShrink: 0,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
      {hasOverflow && (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          sx={{
            mt: 0.5,
            alignSelf: "flex-start",
            px: 0.75,
            minHeight: 0,
            color: "primary.dark",
          }}
        >
          {expanded
            ? "Show less"
            : `Show ${rows.length - LEADERBOARD_LIMIT} more`}
        </Button>
      )}
    </Box>
  );
}

// Collapsed by default once the list outgrows one page, so a mature
// account's report doesn't open on a long table.
const SC_TABLE_PAGE = 10;

const SC_COLUMNS: DataTableColumn<ScEvaluationRow & { sn: number }>[] = [
  { field: "sn", header: "S/N", width: 56, align: "right" },
  { field: "originator", header: "Originator", width: 180, tooltip: true },
  { field: "employeeId", header: "Employee ID", width: 120 },
  {
    field: "suggestionNumber",
    header: "Suggestion No",
    width: 140,
    render: (r) => r.suggestionNumber || "—",
  },
  { field: "department", header: "Department", width: 150, tooltip: true },
  {
    field: "rating",
    header: "SC rating %",
    width: 110,
    align: "right",
    render: (r) => `${r.rating}%`,
  },
  {
    field: "rewardCategory",
    header: "Reward category",
    width: 130,
    align: "center",
    render: (r) => r.rewardCategory || "—",
  },
  { field: "remark", header: "Remark", width: 240, tooltip: true },
];

// Indorama's 2024 Steering Committee report layout: one row per
// suggestion the SC scored, highest rating first.
function ScEvaluationsTable({ rows }: { rows: ScEvaluationRow[] }) {
  // null = not toggled yet: follow the row count, which arrives after mount.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? rows.length <= SC_TABLE_PAGE;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(SC_TABLE_PAGE);
  const numbered = rows.map((r, idx) => ({ ...r, sn: idx + 1 }));
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(numbered.length / rowsPerPage) - 1),
  );
  return (
    <Box sx={{ mt: 4, pt: 4, borderTop: "1px solid", borderColor: "divider" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            SC Evaluations
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length === 0
              ? "No SC evaluations in this period."
              : `${rows.length} scored by the Steering Committee, highest rating first`}
          </Typography>
        </Box>
        {rows.length > 0 && (
          <Button
            size="small"
            onClick={() => setToggled(!open)}
            aria-expanded={open}
            sx={{ flexShrink: 0, px: 0.75, minHeight: 0, color: "primary.dark" }}
          >
            {open ? "Hide table" : "Show table"}
          </Button>
        )}
      </Stack>
      {open && rows.length > 0 && (
        <Table
          columns={SC_COLUMNS}
          rows={numbered.slice(
            safePage * rowsPerPage,
            (safePage + 1) * rowsPerPage,
          )}
          rowKey="id"
          page={safePage}
          rowsPerPage={rowsPerPage}
          total={numbered.length}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      )}
    </Box>
  );
}

export default function AnnualSscReport({
  summary,
  periodLabel,
}: AnnualSscReportProps) {
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2.5 }}
      >
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, color: "text.primary" }}
        >
          Committee Report
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {periodLabel}
        </Typography>
      </Stack>

      {/* Report buckets — the same words the XLSX and print use. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(8, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {REPORT_BUCKETS.map((b) => (
          <SummaryStat key={b.key} label={b.label} value={summary.buckets[b.key]} />
        ))}
        <SummaryStat label="SLA breaches" value={summary.slaBreaches} />
      </Box>

      {/* Four leaderboards, separated from the figures by a hairline. */}
      <Box
        sx={{
          mt: 4,
          pt: 4,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 4, md: 5 },
        }}
      >
        <Leaderboard
          title="Best Suggestions"
          caption="SC rating"
          rows={summary.scEvaluations.map((r) => ({
            label: r.suggestionNumber
              ? `${r.suggestionNumber} · ${r.title}`
              : r.title,
            value: `${r.rating}%`,
          }))}
          emptyHint="No SC evaluations in this period."
        />
        <Leaderboard
          title="Most Approved per Person"
          caption="Approved by SC"
          rows={summary.mostApprovedPerPerson.map((p) => ({
            label: `${p.name} (${p.employeeId})`,
            value: p.approved,
          }))}
          emptyHint="No suggestions approved by SC in this period."
        />
        <Leaderboard
          title="Top Units"
          caption="Approved by SC"
          rows={summary.topUnits.map((u) => ({
            label: u.unit,
            value: u.approved,
          }))}
          emptyHint="No suggestions approved by SC in this period."
        />
        <Leaderboard
          title="Top Departments"
          caption="Suggestions received"
          rows={summary.topDepartments.map((d) => ({
            label: displayDepartment(d.department),
            value: d.count,
          }))}
          emptyHint="No suggestions submitted in this period."
        />
      </Box>

      <ScEvaluationsTable rows={summary.scEvaluations} />
    </Box>
  );
}
